import { ucs2decode } from 'punycode/';
import { Subject } from 'rxjs';
import twitterText from 'twitter-text';
import utf8 from 'utf8';

import { EditorConfig } from '../../editor-config';
import { UserMentionPlaceholderEnum } from '../../enums';
import { LinkInMessage } from '../../models/misc.model';
import GenericUtils from 'src/app/shared/utils/generic.utils';
import { Attachments } from './attachments';
import { SingleMessages } from './single-messages';

/**
 * Represents a single message as edited by the answer editor.
 * An answer can one or more single messages.
 * Only twitter public answers can have more than one message.
 */
export class SingleMessage {
  messagesHandler: SingleMessages;

  attachments: Attachments;
  private pText: string; // accessed with a getter and setter
  links: LinkInMessage[] = [];
  isDMSwitchChecked = false;
  areLinkedShortened;

  isLast: boolean;
  isFocused: boolean;
  isOnlyMessage: boolean; // we disable the 'delete' button when there is only one singlemessage
  editorConfig: EditorConfig;
  remainingCharsNumber: number;
  isMessageTooLong: boolean;
  doFocusTextarea$ = new Subject<void>();

  constructor(messagesHandler, attachments, editorConfig) {
    this.messagesHandler = messagesHandler;
    this.attachments = attachments;
    this.editorConfig = editorConfig;
    this.areLinkedShortened = editorConfig.isLinkShortenerEnabled;
    this.text = ''; // this needs to go last as the setter uses this.messagesHandler and this.editorConfig
  }

  get text() {
    return this.pText;
  }

  // use a setter so that updates from ngModel trigger the remaining char recompute
  set text(text) {
    this.pText = text;
    this.checkAndUpdateValidity();
  }

  addMessage() {
    this.doFocus(); // try to avoid keyboard starting to disappear while angular is creating the new textarea
    this.messagesHandler.addMessageAfter(this);
  }

  removeMessage() {
    this.messagesHandler.removeMessage(this);
  }

  didFocus() {
    this.messagesHandler.updateIsFocused(this);
  }

  doFocus() {
    this.doFocusTextarea$.next();
  }

  doFocusAfterTimeout() {
    setTimeout(() => this.doFocus());
  }

  toggleDMSwitch() {
    this.isDMSwitchChecked = !this.isDMSwitchChecked;
    this.checkAndUpdateValidity();
  }

  isValid() {
    const { hasMaxCharsNumber, hasMaxUTF8BytesNumber } = this.editorConfig;
    const isNotEmpty = !!this.text || (this.attachments.hasAttachment() && !this.attachments.hasAgentShortcut());
    const isShortEnough = hasMaxCharsNumber || hasMaxUTF8BytesNumber ? !this.isMessageTooLong : true;
    const isMediaUploadRunning = this.attachments.uploading$.value;

    return isNotEmpty && isShortEnough && !isMediaUploadRunning;
  }

  forceHideAttachments(forceHideAttachments: boolean) {
    this.attachments.setForceHideAttachments(forceHideAttachments);
  }

  private checkAndUpdateValidity() {
    if (this.editorConfig.hasMaxCharsNumber) {
      if (this.editorConfig.useTwitterPublicEditor) {
        this.updateTweetCharactersCounter();
      } else {
        this.updateUnicodeCharsCount();
      }
    } else if (this.editorConfig.hasMaxUTF8BytesNumber) {
      this.updateUTF8BytesCount();
    } else {
      this.isMessageTooLong = false; // in case we just switched from public to private
    }
    this.messagesHandler.updateValidity();
  }

  private updateTweetCharactersCounter() {
    let textWithDMLink = this.text;
    if (this.isDMSwitchChecked) {
      // see https://business.twitter.com/en/help/campaign-editing-and-optimization/public-to-private-conversation.html
      // the link will be shortened and hence use 22 characters
      textWithDMLink += ' https://twitter.com/messages/compose?recipient_id=00000000';
    }

    const textNoPlaceholders = this.removePlaceholders(textWithDMLink);
    const lengthPlaceholders = this.getPlaceholdersHypotheticalLength(textWithDMLink);
    const parsed = twitterText.parseTweet(textNoPlaceholders);

    this.remainingCharsNumber = this.editorConfig.maxCharsNumber - parsed.weightedLength - lengthPlaceholders;
    this.isMessageTooLong = parsed.validRangeEnd < parsed.displayRangeEnd;
  }

  private updateUnicodeCharsCount() {
    // this.text.length would be the number of UCS-2 16-bits words, whereas we need the number of Unicode characters
    // see https://mathiasbynens.be/notes/javascript-encoding for a great explanation

    const textNoPlaceholders = this.removePlaceholders(this.text);
    const lengthPlaceholders = this.getPlaceholdersHypotheticalLength(this.text);
    const editorTextCPs = ucs2decode(textNoPlaceholders);

    this.remainingCharsNumber = this.editorConfig.maxCharsNumber - editorTextCPs.length - lengthPlaceholders;
    this.isMessageTooLong = this.remainingCharsNumber < 0;
  }

  private updateUTF8BytesCount() {
    // ut8.encode(input) returns a string, in which each character is created from one of the bytes obtained by encoding input to UTF-8
    const textNoPlaceholders = this.removePlaceholders(this.text);
    const lengthPlaceholders = this.getPlaceholdersHypotheticalLength(this.text);
    const encoded = utf8.encode(textNoPlaceholders);

    this.remainingCharsNumber = this.editorConfig.maxUTF8BytesNumber - encoded.length - lengthPlaceholders;
    this.isMessageTooLong = this.remainingCharsNumber < 0;
  }

  private getPlaceholdersRegex() {
    const firstNameRegex = GenericUtils.escapeRegexString(UserMentionPlaceholderEnum.user);
    const mentionRegex = GenericUtils.escapeRegexString(UserMentionPlaceholderEnum.mention);
    const userRegex = GenericUtils.escapeRegexString(UserMentionPlaceholderEnum.firstName);
    return new RegExp(`${firstNameRegex}|${mentionRegex}|${userRegex}`, 'g');
  }

  private removePlaceholders(text: string) {
    return text.replace(this.getPlaceholdersRegex(), '');
  }

  /**
   * Front can't know the real length of a paceholder, because it is replaced by backend.
   * So we consider a random length of 25 (see comments in SP-5972)
   */
  private getPlaceholdersHypotheticalLength(text: string) {
    const matches = text.match(this.getPlaceholdersRegex())?.length || 0;
    return matches * 25;
  }
}
